import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { TwoFactorEnrollment } from "@/components/features/security/two-factor-enrollment";
import { setEnrollmentRedirectSuppressed } from "@/services/apiClient";

/**
 * The gate that makes mandatory 2FA actually mandatory.
 *
 * WHY A GATE AND NOT A PAGE. Enrollment used to be the /security/two-factor
 * route, reached by the apiClient's redirect on the enrollment code. A route
 * can be left: click the sidebar and you are back in the product, and
 * anything that does not happen to call a gated endpoint keeps working. The
 * server was never fooled — every professional endpoint still refused — but
 * the app LOOKED usable, which is worse than being refused.
 *
 * Mounted in AppLayout rather than per page, so it covers every authenticated
 * route by construction. A list of pages to protect is a list to forget to
 * add to.
 *
 * IT KEYS ON THE SERVER'S VERDICT, NOT ON `enrolled`. Enforcement is env-driven
 * (AUTH_TOTP_ENFORCED) and deliberately defaults off, so blocking on
 * `!enrolled` would lock out every un-enrolled professional the moment this
 * shipped — the exact failure that flag exists to prevent. The server answers
 * `enrollmentRequired` with the same two terms its guard uses.
 */
export function TwoFactorEnrollmentGate({ open }: { open: boolean }): JSX.Element | null {
  const queryClient = useQueryClient();

  /*
    While the modal is up, the apiClient must NOT answer a 403 by navigating.
    `window.location.href` is a full reload: a stray refusal from anything
    still in flight would wipe the six digits someone is halfway through
    typing.
  */
  useEffect(() => {
    setEnrollmentRedirectSuppressed(open);
    return () => setEnrollmentRedirectSuppressed(false);
  }, [open]);

  if (!open) return null;

  return (
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80" />
        {/* Radix primitives directly, not our DialogContent: that one always
            renders a close button, and there is nothing to close to. Escape
            and outside clicks are refused for the same reason — a gate with
            an exit is a suggestion. */}
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 grid max-h-[90svh] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg sm:rounded-lg"
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <TwoFactorEnrollment
            onDone={() => {
              /*
                EVERYTHING is invalidated, not just the status.

                While the gate held, every professional call this session
                made was refused — so whatever the cache holds for them is
                either missing or an error, and the list behind the modal
                would otherwise come back exactly as empty as the refusals
                left it. The status query is invalidated by the same sweep,
                and re-reading it (rather than assuming success) is what
                closes the modal: it opened on the server's verdict and it
                closes on the server's verdict.
              */
              void queryClient.invalidateQueries();
            }}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
